import { filterDOMProps, useEvent } from '@react-aria/utils';
import clsx from 'clsx';
import React from 'react';

import { Icon } from '@gitbook/spine-icons';
import { useMergedRefs } from '@gitbook/util-react-hooks';

import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { HeadlessTextInput } from '../TextInput';
import { Toolbar } from '../Toolbar';
import { Tooltip } from '../Tooltip';
import { renderIcon } from '../util';
import { getTokenStyle, type TokenStyle } from './syntax/shiki';

export const CODE_BLOCK_DEFAULT_COLLAPSED_LINE_COUNT = 10;

export interface CodeBlockProps {
    /** Code block content, typically composed with {@link CodeBlockBody} and code lines. */
    children: React.ReactNode;
    /** Additional class names for layout overrides at the call site. */
    className?: string;
    /** Visual treatment for the code block. Use `embedded` when a parent component owns the frame and background. */
    kind?: 'default' | 'embedded';
}

export interface CodeBlockConfiguration {
    collapsedLineCount: number;
    editable: boolean;
    expandable: boolean;
    numberOfLines?: number;
    overflow: 'scroll' | 'wrap';
    /** Whether each line reserves and renders the left gutter for line numbers and line actions. */
    showLineGutter: boolean;
    showLineNumbers: boolean;
}

export interface CodeBlockState {
    isExpanded: boolean;
    bodyId?: string;
    isTyping: boolean;
}

export type CodeBlockContextType = CodeBlockConfiguration & CodeBlockState;

const CodeBlockContext = React.createContext<CodeBlockContextType>({
    collapsedLineCount: CODE_BLOCK_DEFAULT_COLLAPSED_LINE_COUNT,
    editable: false,
    overflow: 'scroll',
    showLineGutter: true,
    showLineNumbers: false,
    numberOfLines: undefined,
    expandable: false,
    isExpanded: false,
    isTyping: false,
});

/**
 * Component to render a code block.
 */
export function CodeBlock({
    ref: forwardedRef,
    ...props
}: (CodeBlockProps & Partial<CodeBlockContextType>) & {
    ref?: React.Ref<HTMLDivElement>;
}): React.ReactElement {
    const {
        children,
        className,
        kind = 'default',
        collapsedLineCount = CODE_BLOCK_DEFAULT_COLLAPSED_LINE_COUNT,
        editable = false,
        expandable = false,
        isExpanded = false,
        numberOfLines,
        overflow = 'scroll',
        showLineGutter = true,
        showLineNumbers = false,
    } = props;

    const bodyId = React.useId();
    const [isTyping, setIsTyping] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>(null);

    const setCodeBlockRef = useMergedRefs(ref, forwardedRef);

    // Listen on the document, so it works also in Slate editor,
    // that has the focus.
    const documentRef = React.useRef(window.document);
    useEvent(
        documentRef,
        'keydown',
        (event) => {
            if (
                editable &&
                ref.current &&
                event.target instanceof HTMLElement &&
                (ref.current === event.target || event.target.contains(ref.current))
            ) {
                // Hide the toolbar while typing to avoid blocking interactions with code content.
                setIsTyping(true);
            }
        },
        {
            capture: true,
            passive: true,
        }
    );

    const value = React.useMemo(
        () => ({
            bodyId,
            collapsedLineCount,
            editable,
            expandable: Boolean(expandable),
            isExpanded,
            numberOfLines,
            overflow,
            showLineGutter,
            showLineNumbers,
            isTyping,
        }),
        [
            collapsedLineCount,
            showLineNumbers,
            showLineGutter,
            editable,
            overflow,
            numberOfLines,
            expandable,
            bodyId,
            isExpanded,
            isTyping,
        ]
    );

    return (
        <CodeBlockContext.Provider value={value}>
            <div
                ref={setCodeBlockRef}
                onMouseMove={() => {
                    if (editable) {
                        setIsTyping(false);
                    }
                }}
                className={clsx(
                    'group/code-block relative w-full select-none',
                    kind === 'default'
                        ? 'rounded-sm border border-base bg-(--shiki-background) py-2'
                        : null,
                    className
                )}
            >
                {children}
            </div>
        </CodeBlockContext.Provider>
    );
}
const EXPAND_ANIMATION_THRESHOLD = 20;

export function CodeBlockBody(props: { children: React.ReactNode }) {
    const { children } = props;
    const { collapsedLineCount, expandable, isExpanded, bodyId, numberOfLines } =
        React.useContext(CodeBlockContext);
    const differenceInLineCount = numberOfLines ? numberOfLines - collapsedLineCount : 0;
    const isAnimated = expandable && differenceInLineCount <= EXPAND_ANIMATION_THRESHOLD;
    const collapsedHeight = `${collapsedLineCount * 1.5}rem`; // assuming line height of 1.5rem
    return (
        <React.Suspense fallback={null}>
            <div
                className={clsx(
                    'w-full overflow-auto',
                    expandable && [
                        'overflow-y-hidden [transition-behavior:allow-discrete]',
                        isAnimated &&
                            'motion-safe:transition-[height] motion-safe:delay-100 motion-safe:duration-100',
                        isExpanded
                            ? '[height:calc-size(auto,size)]'
                            : '[mask-image:linear-gradient(to_bottom,black_0%,black_55%,transparent_100%)]',
                    ]
                )}
                style={expandable && !isExpanded ? { height: collapsedHeight } : {}}
                id={bodyId}
            >
                <CodeBlockContent>{children}</CodeBlockContent>
            </div>
        </React.Suspense>
    );
}

function CodeBlockContent(props: { children: React.ReactNode }) {
    const { children } = props;

    const { overflow, showLineGutter } = React.useContext(CodeBlockContext);
    return (
        <div
            translate="no"
            spellCheck={false}
            className={clsx(
                'grid w-full [count-reset:line] [tab-size:2] print:whitespace-pre-wrap',
                showLineGutter ? 'grid-cols-[auto_minmax(0,_1fr)]' : 'grid-cols-[minmax(0,_1fr)]',
                overflow === 'scroll' ? 'min-w-max' : 'min-w-full'
            )}
        >
            {children}
        </div>
    );
}

/**
 * Optional header for the code block.
 */
export function CodeBlockHeader(props: { children: React.ReactNode }) {
    return (
        <div
            contentEditable={false}
            className="size-full w-3/4 pr-4 pl-5 text-ui-base text-wrap text-muted select-none"
            {...props}
        />
    );
}

/**
 * Resize the input element based on its content. If the browser supports it, use the `field-sizing: content` CSS property instead.
 */
export function CodeBlockEditableTitle(props: {
    title?: string;
    onChange?: (title: string) => void;
    onEscape?: () => void;
}) {
    const { title, onChange, onEscape } = props;
    const supportsFieldSizing = CSS.supports('field-sizing: content');

    const [inputValue, setInputValue] = React.useState(title);
    return (
        <div
            data-value={inputValue}
            className={clsx(
                'relative',
                // if browser does not support field-sizing:content, the simplest way to resize the input properly is to use grid and a pseudo element with the input value as its content.
                // tailwind not-supports doesn't seem to work with pseudo-elements.
                !supportsFieldSizing &&
                    'grid *:[grid-area:1/1/2/2] after:invisible after:block after:whitespace-pre-wrap after:content-[attr(data-value)_"_"] after:[grid-area:1/1/2/2]'
            )}
        >
            <HeadlessTextInput
                inputClassName="min-w-full resize-none text-muted text-ui-base supports-[field-sizing:content]:[field-sizing:content]" // field-sizing:content support is still limited as of 10/2025
                placeholder="Enter a filename"
                multiline
                rows={1}
                onChange={(event) => setInputValue(event.target.value)}
                onBlur={() => onChange?.(inputValue ?? '')}
                value={inputValue ?? ''}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        onEscape?.();
                    }
                }}
            />
        </div>
    );
}

/**
 * Optional footer for the code block. Not part of editable content.
 */
export function CodeBlockFooter(props: { children: React.ReactNode; className?: string }) {
    return <div contentEditable={false} {...props} />;
}

/**
 * Footer with expand/collapse button for the code block.
 */
export function CodeBlockExpandableFooter(props: { onChange: (isExpanded: boolean) => void }) {
    const { numberOfLines, isExpanded, bodyId } = React.useContext(CodeBlockContext);
    return (
        <CodeBlockFooter className="pointer-events-none absolute bottom-2 flex w-full justify-center">
            <Button
                label={isExpanded ? 'Collapse' : `See all ${numberOfLines} lines`}
                leadingIcon={renderIcon(Icon.ChevronDown, {
                    className: clsx(
                        'origin-center transition-transform will-change-transform',
                        isExpanded ? 'rotate-180' : ''
                    ),
                })}
                size="small"
                kind="secondary"
                onPress={() => {
                    props.onChange(!isExpanded);
                }}
                className="pointer-events-auto opacity-0 group-hover/code-block:opacity-100 focus:opacity-100"
                aria-expanded={isExpanded}
                aria-controls={bodyId}
            />
        </CodeBlockFooter>
    );
}

/**
 * Toolbar container for the code block. Non-editable.
 */
export function CodeBlockToolbar(props: { children: React.ReactNode; show?: boolean }) {
    const { children, show = false } = props;
    const { isTyping } = React.useContext(CodeBlockContext);

    return (
        <div className="@container pointer-events-none absolute inset-0" contentEditable={false}>
            <Toolbar
                size="small"
                className={clsx(
                    'absolute top-2 right-2 flex items-center gap-1 transition-all supports-[container-type:inline-size]:hidden supports-[container-type:inline-size]:@[12rem]:flex',
                    show
                        ? 'pointer-events-auto'
                        : isTyping
                          ? 'pointer-events-none opacity-0'
                          : 'pointer-events-auto opacity-0 group-hover/code-block:opacity-100'
                )}
            >
                {children}
            </Toolbar>
        </div>
    );
}

export interface CodeToken extends TokenStyle {
    content: string;
}

interface CodeBlockLineProps {
    tokens?: CodeToken[];
    children?: React.ReactNode;
    number: number;
    onSelect?: () => void;
    highlighting?:
        | 'highlighted'
        | 'added'
        | 'deleted'
        | 'modified'
        | 'added-highlighted'
        | 'deleted-highlighted'
        | null;
}

/**
 * Renderer for a single line in a code block.
 */
export function CodeBlockLine({
    ref,
    ...props
}: CodeBlockLineProps & { ref?: React.Ref<HTMLDivElement> }) {
    const { number, tokens, children, highlighting, onSelect, ...domProps } = props;
    const { overflow, showLineGutter, showLineNumbers, editable } =
        React.useContext(CodeBlockContext);
    return (
        <div
            {...(domProps ? filterDOMProps(domProps) : {})}
            ref={ref}
            className={clsx(
                'group/code-block-line grid min-w-full font-mono text-ui-base leading-6 font-medium select-none',
                showLineGutter
                    ? 'col-span-2 grid-cols-subgrid'
                    : 'col-span-1 grid-cols-[minmax(0,_1fr)]',
                { wrap: 'whitespace-pre-wrap', scroll: 'pr-4 whitespace-pre' }[overflow],
                highlighting
                    ? {
                          highlighted: 'bg-neutral-ui-active',
                          added: 'bg-diff-added',
                          deleted: 'bg-diff-deleted',
                          modified: 'bg-diff-modified',
                          'added-highlighted': 'bg-diff-added-active',
                          'deleted-highlighted': 'bg-diff-deleted-active',
                      }[highlighting]
                    : null,
                editable && getLineHoverPreviewClass(highlighting)
            )}
        >
            {showLineGutter ? (
                <CodeBlockLineGutter onSelect={onSelect} highlighting={highlighting}>
                    {showLineNumbers ? <CodeBlockLineNumber number={number} /> : '\u200B'}
                </CodeBlockLineGutter>
            ) : null}
            <div className="w-full min-w-0 select-text">
                {tokens ? <CodeBlockLineTokens tokens={tokens} /> : children}
            </div>
        </div>
    );
}

/**
 * Hover preview of what toggling the highlight checkbox would do to the line:
 * for diff lines, swap to the inverse scale (3 ↔ 5) so writers see the toggle's
 * effect without committing. Returns `null` for states where a preview is not
 * meaningful (`'highlighted'` neutral toggle, `'modified'` from version-diff).
 */
function getLineHoverPreviewClass(highlighting: CodeBlockLineProps['highlighting']): string | null {
    switch (highlighting) {
        case 'added':
            return 'has-[[data-line-gutter]:hover]:bg-diff-added-active';
        case 'deleted':
            return 'has-[[data-line-gutter]:hover]:bg-diff-deleted-active';
        case 'added-highlighted':
            return 'has-[[data-line-gutter]:hover]:bg-diff-added';
        case 'deleted-highlighted':
            return 'has-[[data-line-gutter]:hover]:bg-diff-deleted';
        case 'highlighted':
        case 'modified':
            return null;
        default:
            return 'hover:bg-neutral-ui-hover';
    }
}

/**
 * Renders the syntax highlighted tokens for a code block line.
 */
function CodeBlockLineTokens(props: { tokens: CodeToken[] }) {
    const { tokens } = props;
    return (
        <>
            {tokens.map((token, tokenIndex) => (
                <span key={tokenIndex} style={getTokenStyle(token)}>
                    {token.content}
                </span>
            ))}
        </>
    );
}

/**
 * Left gutter for a code block line, optionally with a line highlight checkbox. Not content-editable.
 */
function CodeBlockLineGutter(props: {
    children: React.ReactNode;
    onSelect?: () => void;
    highlighting?: CodeBlockLineProps['highlighting'];
}) {
    const { children, onSelect, highlighting } = props;

    const { editable } = React.useContext(CodeBlockContext);
    // The checkbox reflects the manual `highlighted` axis only — both `'highlighted'`
    // alone and any `*-highlighted` combined variant (when notation stacks with manual
    // highlight) count as "the writer enabled highlight on this line".
    const isHighlighted =
        highlighting === 'highlighted' ||
        highlighting === 'added-highlighted' ||
        highlighting === 'deleted-highlighted';
    return (
        <div
            data-line-gutter
            onClick={onSelect}
            className={clsx(
                'group/code-block-line-gutter relative flex w-full min-w-0 justify-end pr-3 pl-2 select-none',
                {
                    'cursor-pointer': editable,
                    'cursor-default': !editable,
                }
            )}
            contentEditable={false}
        >
            {children}
            {editable ? (
                <div className="invisible absolute inset-0 flex pt-[0.5lh] pl-0.75 opacity-0 transition-opacity transition-discrete group-hover/code-block-line-gutter:visible group-hover/code-block-line-gutter:opacity-100">
                    <Tooltip label="Highlight line" position="bottom">
                        <Checkbox
                            size="xsmall"
                            value={isHighlighted}
                            onChange={onSelect}
                            className="-translate-y-1/2"
                        />
                    </Tooltip>
                </div>
            ) : null}
        </div>
    );
}

/**
 * Renders the line number for a code block line.
 */
function CodeBlockLineNumber({ number }: { number: number }) {
    return <span className="mr-2 font-mono text-muted tabular-nums select-none">{number}</span>;
}

interface VirtualizedCodeBlockLineProps {
    children: React.ReactNode;
    attributes?: React.HTMLAttributes<HTMLDivElement>;
    style?: React.CSSProperties;
}

/**
 * A simplified code block line, rendered with virtualization.
 */
export function VirtualizedCodeBlockLine({
    ref,
    ...props
}: VirtualizedCodeBlockLineProps & { ref?: React.Ref<HTMLDivElement> }) {
    const { showLineGutter, showLineNumbers, numberOfLines } = React.useContext(CodeBlockContext);
    return (
        <div
            {...props.attributes}
            className={clsx(
                'grid min-w-full font-mono text-ui-small leading-6 font-medium whitespace-pre-wrap select-none',
                showLineGutter
                    ? 'col-span-2 grid-cols-subgrid'
                    : 'col-span-1 grid-cols-[minmax(0,_1fr)]'
            )}
            style={props.style}
            data-virtual
            ref={ref}
        >
            {showLineGutter ? (
                <div
                    aria-hidden
                    className={clsx('pr-3 pl-2 tabular-nums', showLineNumbers && 'mr-2')}
                >
                    {showLineNumbers && numberOfLines ? (
                        <CodeBlockLineNumber number={numberOfLines} />
                    ) : (
                        '\u200B'
                    )}
                </div>
            ) : null}
            {props.children}
        </div>
    );
}
